import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Lock, CheckCircle, AlertCircle, GripVertical, Wallet, Package } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

// Sortable Deduction Row Component
const SortableDeductionRow = ({ deduction, canEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: deduction.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      {canEdit && (
        <TableCell className="w-10">
          <button {...attributes} {...listeners} className="cursor-grab hover:bg-gray-100 p-1 rounded">
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
        </TableCell>
      )}
      <TableCell className="font-medium">{deduction.category}</TableCell>
      <TableCell>₹{parseFloat(deduction.amount).toFixed(2)}</TableCell>
      <TableCell>{deduction.remarks || '-'}</TableCell>
      {canEdit && (
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(deduction.id, deduction.category)}
            data-testid={`delete-deduction-${deduction.id}`}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
};

const DayClosingForm = ({ user }) => {
  const { closingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [deductionDialog, setDeductionDialog] = useState(false);
  const [deleteDeductionDialog, setDeleteDeductionDialog] = useState(false);
  const [deductionToDelete, setDeductionToDelete] = useState(null);
  const [existingClosingId, setExistingClosingId] = useState(closingId || null);
  const [closeDayDialog, setCloseDayDialog] = useState(false);
  
  // Edit remarks for updating existing closing
  const [editRemarksDialog, setEditRemarksDialog] = useState(false);
  const [editRemarks, setEditRemarks] = useState('');
  const [isExistingClosing, setIsExistingClosing] = useState(false);
  
  // Vendor Bills for cashier payment - separated by period
  const [vendorBillDialog, setVendorBillDialog] = useState(false);
  const [vendorBillType, setVendorBillType] = useState('today'); // 'today', 'this_month', 'old_month'
  const [todayBills, setTodayBills] = useState([]);
  const [thisMonthBills, setThisMonthBills] = useState([]);
  const [oldMonthBills, setOldMonthBills] = useState([]);
  const [payableBills, setPayableBills] = useState([]);
  const [selectedBills, setSelectedBills] = useState({});
  const [billPaymentAmounts, setBillPaymentAmounts] = useState({});
  const [loadingBills, setLoadingBills] = useState(false);
  
  // Vendor filter for This Month and Old Month bills
  const [availableVendors, setAvailableVendors] = useState([]);
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('');
  
  // Opening Liabilities for cashier
  const [openingLiabilities, setOpeningLiabilities] = useState([]);
  const [selectedLiabilities, setSelectedLiabilities] = useState({});
  const [liabilityPaymentAmounts, setLiabilityPaymentAmounts] = useState({});
  const [liabilityDialog, setLiabilityDialog] = useState(false);
  
  // Salary Payment for cashier
  const [salaryDialog, setSalaryDialog] = useState(false);
  const [salaryPaymentType, setSalaryPaymentType] = useState('this_month'); // 'this_month', 'old_months', 'advance'
  const [thisMonthSalaries, setThisMonthSalaries] = useState([]);
  const [oldMonthSalaries, setOldMonthSalaries] = useState([]);
  const [employeesForAdvance, setEmployeesForAdvance] = useState([]);
  const [selectedSalaries, setSelectedSalaries] = useState({});
  const [salaryPaymentAmounts, setSalaryPaymentAmounts] = useState({});
  const [advanceEmployee, setAdvanceEmployee] = useState('');
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [loadingSalaries, setLoadingSalaries] = useState(false);
  const [payingSalaries, setPayingSalaries] = useState(false);
  
  // Loading states to prevent double-click submissions
  const [addingDeduction, setAddingDeduction] = useState(false);
  const [deletingDeduction, setDeletingDeduction] = useState(false);
  const [payingBills, setPayingBills] = useState(false);
  const [payingLiabilities, setPayingLiabilities] = useState(false);
  const [closingDay, setClosingDay] = useState(false);
  
  // Utility Bills for cashier payment
  const [utilityBillDialog, setUtilityBillDialog] = useState(false);
  const [utilityBillTypes, setUtilityBillTypes] = useState([]);
  const [pendingUtilityBills, setPendingUtilityBills] = useState([]);
  const [selectedUtilityBills, setSelectedUtilityBills] = useState({});
  const [utilityPaymentAmounts, setUtilityPaymentAmounts] = useState({});
  const [loadingUtilityBills, setLoadingUtilityBills] = useState(false);
  const [payingUtilityBills, setPayingUtilityBills] = useState(false);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const [closingData, setClosingData] = useState({
    date: new Date().toISOString().split('T')[0],
    outlet: user.outlet || '',
    opening_cash: 0,
    cash_received: 0,  // Auto-filled from bank cash withdrawals
    gross_sales: 0,
    extra_liquor_sale: 0,
    extra_other_sale: 0,
    nc_amount: 0,  // NC (No Charge/Non-Chargeable) amount
    discounts_given: 0,
    cash_deposit_to_bank: 0,  // Cash deposit to bank 592 - NOT an expense
    notes: '',
    status: 'draft',
    total_sales: 0,
    total_deductions: 0,
    closing_cash: 0
  });
  
  // Prevent double-click for cash deposit
  const [depositingCash, setDepositingCash] = useState(false);

  const [deductionForm, setDeductionForm] = useState({
    group: '',
    category: '',
    amount: 0,
    remarks: ''
  });

  // Check for existing closing on mount if no ID provided
  useEffect(() => {
    const checkExistingClosing = async () => {
      if (!closingId) {
        try {
          const token = localStorage.getItem('token');
          const today = new Date().toISOString().split('T')[0];
          const response = await axios.get(`${API}/dayclosings`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { start_date: today, end_date: today }
          });
          if (response.data.length > 0) {
            // Found existing closing for today, redirect to edit it
            const existingId = response.data[0].id;
            setExistingClosingId(existingId);
            navigate(`/day-closing/${existingId}`, { replace: true });
          } else {
            // No closing for today, fetch previous day's closing cash as opening cash
            await fetchPreviousClosingCash();
          }
        } catch (error) {
          console.error('Failed to check existing closing');
        }
      }
      setInitialLoading(false);
    };
    checkExistingClosing();
  }, [closingId, navigate]);

  // Fetch previous day's closing cash to use as opening cash
  const fetchPreviousClosingCash = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get the most recent approved or closed day closing
      const response = await axios.get(`${API}/dayclosings/previous-closing-cash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.closing_cash !== undefined) {
        setClosingData(prev => ({
          ...prev,
          opening_cash: response.data.closing_cash
        }));
      }
    } catch (error) {
      console.error('Failed to fetch previous closing cash');
    }
  };

  // Fetch cash withdrawals from bank for a specific date
  const fetchCashWithdrawals = async (date) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-cash-withdrawals/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.total || 0;
    } catch (error) {
      console.error('Failed to fetch cash withdrawals');
      return 0;
    }
  };

  useEffect(() => {
    fetchCategories();
    const idToUse = closingId || existingClosingId;
    if (idToUse) {
      fetchClosing(idToUse);
      fetchDeductions(idToUse);
    }
  }, [closingId, existingClosingId]);

  // Fetch cash withdrawals when date changes OR when loading existing closing
  useEffect(() => {
    const updateCashReceived = async () => {
      if (closingData.date) {
        const cashReceived = await fetchCashWithdrawals(closingData.date);
        setClosingData(prev => ({
          ...prev,
          cash_received: cashReceived
        }));
      }
    };
    updateCashReceived();
  }, [closingData.date]);

  useEffect(() => {
    // Calculate totals whenever data changes (including discounts_given and nc_amount as add-ons)
    const totalSales = parseFloat(closingData.gross_sales || 0) + 
                       parseFloat(closingData.extra_liquor_sale || 0) + 
                       parseFloat(closingData.extra_other_sale || 0) +
                       parseFloat(closingData.nc_amount || 0) +
                       parseFloat(closingData.discounts_given || 0);
    const totalDeductions = deductions.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    const cashDeposit = parseFloat(closingData.cash_deposit_to_bank || 0);
    // Closing cash = Opening cash + Cash Received from Bank + Net Sales - Cash Deposit to Bank
    // Cash deposit reduces closing cash because that cash is now in the bank
    const closingCash = parseFloat(closingData.opening_cash || 0) + 
                        parseFloat(closingData.cash_received || 0) + 
                        totalSales - totalDeductions - cashDeposit;
    
    setClosingData(prev => ({
      ...prev,
      total_sales: totalSales,
      total_deductions: totalDeductions,
      closing_cash: closingCash
    }));
  }, [closingData.opening_cash, closingData.cash_received, closingData.gross_sales, closingData.extra_liquor_sale, closingData.extra_other_sale, closingData.nc_amount, closingData.discounts_given, closingData.cash_deposit_to_bank, deductions]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.filter(c => c.is_active));
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchClosing = async (id = closingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dayclosings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const closingDate = response.data.date;
      
      // Fetch cash withdrawals for this closing's date
      const cashReceived = await fetchCashWithdrawals(closingDate);
      
      setClosingData({
        ...response.data,
        cash_received: cashReceived
      });
      
      // Mark as existing closing if it has edit history
      // This means remarks are required for subsequent edits
      const hasEditHistory = response.data.edit_history && response.data.edit_history.length > 0;
      setIsExistingClosing(hasEditHistory);
    } catch (error) {
      toast.error('Failed to fetch day closing');
    }
  };

  const fetchDeductions = async (id = closingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dayclosings/${id}/deductions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeductions(response.data);
    } catch (error) {
      toast.error('Failed to fetch deductions');
    }
  };

  const handleSaveClosing = async (remarksOverride = null) => {
    const currentId = closingId || existingClosingId;
    
    // If updating existing closing and no remarks provided, show dialog
    if (currentId && isExistingClosing && !remarksOverride) {
      setEditRemarksDialog(true);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (currentId) {
        // Update existing - ensure numeric values are floats
        const updateData = {
          opening_cash: parseFloat(closingData.opening_cash) || 0,
          gross_sales: parseFloat(closingData.gross_sales) || 0,
          extra_liquor_sale: parseFloat(closingData.extra_liquor_sale) || 0,
          extra_other_sale: parseFloat(closingData.extra_other_sale) || 0,
          nc_amount: parseFloat(closingData.nc_amount) || 0,
          discounts_given: parseFloat(closingData.discounts_given) || 0,
          cash_deposit_to_bank: parseFloat(closingData.cash_deposit_to_bank) || 0,
          notes: closingData.notes
        };
        
        // Add edit remarks if provided
        if (remarksOverride) {
          updateData.edit_remarks = remarksOverride;
        }
        
        await axios.patch(`${API}/dayclosings/${currentId}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Day closing updated!');
        setEditRemarksDialog(false);
        setEditRemarks('');
        // Mark as existing after first save
        setIsExistingClosing(true);
      } else {
        // Create new - ensure numeric values are floats
        const response = await axios.post(`${API}/dayclosings`, {
          date: closingData.date,
          outlet: closingData.outlet,
          opening_cash: parseFloat(closingData.opening_cash) || 0,
          gross_sales: parseFloat(closingData.gross_sales) || 0,
          extra_liquor_sale: parseFloat(closingData.extra_liquor_sale) || 0,
          extra_other_sale: parseFloat(closingData.extra_other_sale) || 0,
          nc_amount: parseFloat(closingData.nc_amount) || 0,
          discounts_given: parseFloat(closingData.discounts_given) || 0,
          cash_deposit_to_bank: parseFloat(closingData.cash_deposit_to_bank) || 0,
          notes: closingData.notes
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Day closing created!');
        setIsExistingClosing(true);
        navigate(`/day-closing/${response.data.id}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save day closing'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWithRemarks = () => {
    if (!editRemarks.trim()) {
      toast.error('Please enter remarks explaining the changes');
      return;
    }
    handleSaveClosing(editRemarks);
  };

  const handleAddDeduction = async (e) => {
    e.preventDefault();
    if (addingDeduction) return; // Prevent double-click
    
    const currentId = closingId || existingClosingId;
    if (!currentId) {
      toast.error('Please save the day closing first before adding deductions');
      return;
    }

    try {
      setAddingDeduction(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/dayclosings/${currentId}/deductions`, {
        category: deductionForm.category,
        amount: deductionForm.amount,
        remarks: deductionForm.remarks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deduction added!');
      setDeductionDialog(false);
      setDeductionForm({ group: '', category: '', amount: 0, remarks: '' });
      fetchDeductions(currentId);
      fetchClosing(currentId); // Refresh to get updated totals
    } catch (error) {
      toast.error('Failed to add deduction');
    } finally {
      setAddingDeduction(false);
    }
  };

  const handleDeleteDeduction = async (deductionId) => {
    if (deletingDeduction) return; // Prevent double-click
    
    try {
      setDeletingDeduction(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/deductions/${deductionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deduction deleted');
      setDeleteDeductionDialog(false);
      setDeductionToDelete(null);
      const currentId = closingId || existingClosingId;
      fetchDeductions(currentId);
      fetchClosing(currentId); // Refresh to get updated totals
    } catch (error) {
      toast.error('Failed to delete deduction');
    } finally {
      setDeletingDeduction(false);
    }
  };

  // Vendor Bill Payment Functions
  const fetchPayableBills = async () => {
    try {
      setLoadingBills(true);
      const token = localStorage.getItem('token');
      
      // Fetch bills by category: today, current month, old months
      const [todayRes, currentRes, oldRes, liabilitiesRes] = await Promise.all([
        axios.get(`${API}/purchase-bills/payable/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-bills/payable/current-month`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-bills/payable/old-months`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/opening-liabilities/payable/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTodayBills(todayRes.data || []);
      
      // This month bills exclude today's bills
      const todayIds = new Set((todayRes.data || []).map(b => b.id));
      const filteredThisMonth = (currentRes.data || []).filter(b => !todayIds.has(b.id));
      setThisMonthBills(filteredThisMonth);
      
      const oldBills = oldRes.data || [];
      setOldMonthBills(oldBills);
      setOpeningLiabilities(liabilitiesRes.data || []);
      
      // Extract unique vendors from this month and old month bills for vendor filter
      const vendorMap = new Map();
      [...filteredThisMonth, ...oldBills].forEach(bill => {
        if (bill.vendor_id && bill.vendor_name) {
          vendorMap.set(bill.vendor_id, bill.vendor_name);
        }
      });
      const vendors = Array.from(vendorMap.entries()).map(([id, name]) => ({ id, name }));
      vendors.sort((a, b) => a.name.localeCompare(b.name));
      setAvailableVendors(vendors);
      
      // Set initial payable bills based on current type
      switch (vendorBillType) {
        case 'today':
          setPayableBills(todayRes.data || []);
          break;
        case 'this_month':
          setPayableBills(filteredThisMonth);
          break;
        case 'old_month':
          setPayableBills(oldBills);
          break;
        default:
          setPayableBills(todayRes.data || []);
      }
      
      setLoadingBills(false);
    } catch (error) {
      toast.error('Failed to fetch payable bills');
      setLoadingBills(false);
    }
  };
  
  // Update payable bills when type or vendor filter changes
  useEffect(() => {
    if (vendorBillDialog) {
      let bills = [];
      switch (vendorBillType) {
        case 'today':
          bills = todayBills;
          break;
        case 'this_month':
          bills = thisMonthBills;
          // Filter by vendor if selected
          if (selectedVendorFilter) {
            bills = bills.filter(b => b.vendor_id === selectedVendorFilter);
          }
          break;
        case 'old_month':
          bills = oldMonthBills;
          // Filter by vendor if selected
          if (selectedVendorFilter) {
            bills = bills.filter(b => b.vendor_id === selectedVendorFilter);
          }
          break;
        default:
          bills = todayBills;
      }
      setPayableBills(bills);
      setSelectedBills({});
      setBillPaymentAmounts({});
    }
  }, [vendorBillType, vendorBillDialog, todayBills, thisMonthBills, oldMonthBills, selectedVendorFilter]);

  const formatBillDate = (d) => {
    if (!d) return '-';
    const parts = String(d).split('-'); // YYYY-MM-DD
    if (parts.length !== 3) return d;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const mi = parseInt(parts[1], 10) - 1;
    return `${parts[2]} ${months[mi] || parts[1]} ${parts[0]}`;
  };

  const openVendorBillDialog = () => {
    const currentId = closingId || existingClosingId;
    if (!currentId) {
      toast.error('Please save the day closing first');
      return;
    }
    setVendorBillType('today');
    setSelectedVendorFilter('');
    fetchPayableBills();
    setSelectedBills({});
    setBillPaymentAmounts({});
    setVendorBillDialog(true);
  };

  const fetchPayableLiabilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/opening-liabilities/payable/all`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setOpeningLiabilities(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch liabilities');
    }
  };

  const openLiabilityDialog = () => {
    const currentId = closingId || existingClosingId;
    if (!currentId) {
      toast.error('Please save the day closing first');
      return;
    }
    fetchPayableLiabilities();
    setSelectedLiabilities({});
    setLiabilityPaymentAmounts({});
    setLiabilityDialog(true);
  };

  const toggleLiabilitySelection = (liabilityId, balance) => {
    setSelectedLiabilities(prev => {
      const newSelected = { ...prev };
      if (newSelected[liabilityId]) {
        delete newSelected[liabilityId];
        setLiabilityPaymentAmounts(pa => {
          const newAmounts = { ...pa };
          delete newAmounts[liabilityId];
          return newAmounts;
        });
      } else {
        newSelected[liabilityId] = true;
        setLiabilityPaymentAmounts(pa => ({ ...pa, [liabilityId]: balance }));
      }
      return newSelected;
    });
  };

  const updateLiabilityPaymentAmount = (liabilityId, value, maxBalance) => {
    const amount = Math.min(parseFloat(value) || 0, maxBalance);
    setLiabilityPaymentAmounts(prev => ({ ...prev, [liabilityId]: amount }));
  };

  const paySelectedLiabilities = async () => {
    if (payingLiabilities) return; // Prevent double-click
    
    const currentId = closingId || existingClosingId;
    if (!currentId) return;

    const liabilityIds = Object.keys(selectedLiabilities);
    if (liabilityIds.length === 0) {
      toast.error('Please select at least one liability to pay');
      return;
    }

    try {
      setPayingLiabilities(true);
      const token = localStorage.getItem('token');
      
      for (const liabilityId of liabilityIds) {
        const amount = liabilityPaymentAmounts[liabilityId];
        if (amount > 0) {
          await axios.post(`${API}/opening-liabilities/${liabilityId}/pay-cash`, null, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              amount: amount,
              dayclosing_id: currentId,
              remarks: 'Cash payment from Day Closing'
            }
          });
        }
      }

      toast.success('Liability payments recorded as deductions');
      setLiabilityDialog(false);
      setSelectedLiabilities({});
      setLiabilityPaymentAmounts({});
      
      // Refresh data - closing, deductions, AND opening liabilities
      const idToUse = closingId || existingClosingId;
      fetchClosing(idToUse);
      fetchDeductions(idToUse);
      fetchPayableBills(); // Refresh bills to show updated balances
      fetchPayableLiabilities(); // Refresh liabilities to show updated balances
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay liabilities'));
    } finally {
      setPayingLiabilities(false);
    }
  };

  // ===== SALARY PAYMENT FUNCTIONS =====
  
  const fetchSalaryData = async () => {
    try {
      setLoadingSalaries(true);
      const token = localStorage.getItem('token');
      
      const [thisMonthRes, oldMonthsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/salaries/payable/current-month`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/salaries/payable/old-months`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/advances/employees-list`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setThisMonthSalaries(thisMonthRes.data || []);
      setOldMonthSalaries(oldMonthsRes.data || []);
      setEmployeesForAdvance(employeesRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch salary data');
    } finally {
      setLoadingSalaries(false);
    }
  };

  const openSalaryDialog = () => {
    const currentId = closingId || existingClosingId;
    if (!currentId) {
      toast.error('Please save the day closing first');
      return;
    }
    setSalaryPaymentType('this_month');
    fetchSalaryData();
    setSelectedSalaries({});
    setSalaryPaymentAmounts({});
    setAdvanceEmployee('');
    setAdvanceAmount('');
    setSalaryDialog(true);
  };

  const toggleSalarySelection = (salaryId, balance) => {
    setSelectedSalaries(prev => {
      const newSelected = { ...prev };
      if (newSelected[salaryId]) {
        delete newSelected[salaryId];
        setSalaryPaymentAmounts(pa => {
          const newAmounts = { ...pa };
          delete newAmounts[salaryId];
          return newAmounts;
        });
      } else {
        newSelected[salaryId] = true;
        setSalaryPaymentAmounts(pa => ({ ...pa, [salaryId]: balance }));
      }
      return newSelected;
    });
  };

  const updateSalaryPaymentAmount = (salaryId, amount, maxBalance) => {
    const numAmount = parseFloat(amount) || 0;
    setSalaryPaymentAmounts(prev => ({
      ...prev,
      [salaryId]: Math.min(numAmount, maxBalance)
    }));
  };

  const getTotalSalaryPayment = () => {
    return Object.entries(salaryPaymentAmounts)
      .filter(([id]) => selectedSalaries[id])
      .reduce((sum, [, amount]) => sum + (parseFloat(amount) || 0), 0);
  };

  const handlePaySalaries = async () => {
    if (payingSalaries) return;
    
    const selectedIds = Object.keys(selectedSalaries);
    if (selectedIds.length === 0) {
      toast.error('Please select at least one salary');
      return;
    }

    const idToUse = closingId || existingClosingId;
    if (!idToUse) {
      toast.error('Please save the day closing first');
      return;
    }

    setPayingSalaries(true);
    try {
      const token = localStorage.getItem('token');
      const payments = selectedIds.map(salaryId => ({
        salary_id: salaryId,
        amount: salaryPaymentAmounts[salaryId] || 0
      })).filter(p => p.amount > 0);

      await axios.post(`${API}/salaries/pay-cash-batch`, {
        payments: payments,
        dayclosing_id: idToUse
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Paid ${payments.length} salary payments`);
      setSalaryDialog(false);
      setSelectedSalaries({});
      setSalaryPaymentAmounts({});
      
      // Refresh salary data and day closing
      fetchSalaryData();
      fetchClosing(idToUse);
      fetchDeductions(idToUse);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay salaries'));
    } finally {
      setPayingSalaries(false);
    }
  };

  const handlePayAdvance = async () => {
    if (payingSalaries) return;
    
    if (!advanceEmployee || !advanceAmount || parseFloat(advanceAmount) <= 0) {
      toast.error('Please select employee and enter amount');
      return;
    }

    const idToUse = closingId || existingClosingId;
    if (!idToUse) {
      toast.error('Please save the day closing first');
      return;
    }

    setPayingSalaries(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/advances/pay-cash`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          employee_id: advanceEmployee,
          amount: parseFloat(advanceAmount),
          dayclosing_id: idToUse
        }
      });

      toast.success(response.data.message);
      setSalaryDialog(false);
      setAdvanceEmployee('');
      setAdvanceAmount('');
      
      // Refresh salary data and day closing
      fetchSalaryData();
      fetchClosing(idToUse);
      fetchDeductions(idToUse);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay advance'));
    } finally {
      setPayingSalaries(false);
    }
  };

  // Utility Bill Payment Functions
  const fetchUtilityBills = async () => {
    setLoadingUtilityBills(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch utility bill types
      const typesRes = await axios.get(`${API}/utility-bill-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUtilityBillTypes(typesRes.data);
      
      // Fetch pending utility bills
      const billsRes = await axios.get(`${API}/utility-bills/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUtilityBills(billsRes.data || []);
    } catch (error) {
      console.error('Error fetching utility bills:', error);
      setPendingUtilityBills([]);
    } finally {
      setLoadingUtilityBills(false);
    }
  };

  const openUtilityBillDialog = () => {
    const currentId = closingId || existingClosingId;
    if (!currentId) {
      toast.error('Please save the day closing first');
      return;
    }
    fetchUtilityBills();
    setSelectedUtilityBills({});
    setUtilityPaymentAmounts({});
    setUtilityBillDialog(true);
  };

  const toggleUtilityBillSelection = (billId, balance) => {
    setSelectedUtilityBills(prev => {
      const newSelected = { ...prev };
      if (newSelected[billId]) {
        delete newSelected[billId];
        setUtilityPaymentAmounts(pa => {
          const newAmounts = { ...pa };
          delete newAmounts[billId];
          return newAmounts;
        });
      } else {
        newSelected[billId] = true;
        setUtilityPaymentAmounts(pa => ({ ...pa, [billId]: balance }));
      }
      return newSelected;
    });
  };

  const updateUtilityPaymentAmount = (billId, amount, maxBalance) => {
    const numAmount = parseFloat(amount) || 0;
    setUtilityPaymentAmounts(prev => ({
      ...prev,
      [billId]: Math.min(numAmount, maxBalance)
    }));
  };

  const getTotalUtilityPayment = () => {
    return Object.entries(utilityPaymentAmounts)
      .filter(([id]) => selectedUtilityBills[id])
      .reduce((sum, [, amount]) => sum + (parseFloat(amount) || 0), 0);
  };

  const handlePayUtilityBills = async () => {
    if (payingUtilityBills) return;
    
    const selectedIds = Object.keys(selectedUtilityBills);
    if (selectedIds.length === 0) {
      toast.error('Please select at least one utility bill');
      return;
    }

    const idToUse = closingId || existingClosingId;
    if (!idToUse) {
      toast.error('Please save the day closing first');
      return;
    }

    setPayingUtilityBills(true);
    try {
      const token = localStorage.getItem('token');
      const payments = selectedIds.map(billId => ({
        bill_id: billId,
        amount: utilityPaymentAmounts[billId] || 0
      })).filter(p => p.amount > 0);

      await axios.post(`${API}/utility-bills/pay-cash-batch`, {
        payments: payments,
        dayclosing_id: idToUse
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Paid ${payments.length} utility bill(s)`);
      setUtilityBillDialog(false);
      setSelectedUtilityBills({});
      setUtilityPaymentAmounts({});
      
      // Refresh day closing and deductions
      fetchClosing(idToUse);
      fetchDeductions(idToUse);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay utility bills'));
    } finally {
      setPayingUtilityBills(false);
    }
  };

  // Vendor Bill Payment Functions
  const toggleBillSelection = (billId, balance) => {
    setSelectedBills(prev => {
      const newSelected = { ...prev };
      if (newSelected[billId]) {
        delete newSelected[billId];
        setBillPaymentAmounts(pa => {
          const newAmounts = { ...pa };
          delete newAmounts[billId];
          return newAmounts;
        });
      } else {
        newSelected[billId] = true;
        setBillPaymentAmounts(pa => ({ ...pa, [billId]: balance }));
      }
      return newSelected;
    });
  };

  const updateBillPaymentAmount = (billId, amount, maxBalance) => {
    const numAmount = parseFloat(amount) || 0;
    setBillPaymentAmounts(prev => ({
      ...prev,
      [billId]: Math.min(numAmount, maxBalance)
    }));
  };

  const getTotalBillPayment = () => {
    return Object.entries(billPaymentAmounts)
      .filter(([id]) => selectedBills[id])
      .reduce((sum, [, amount]) => sum + (parseFloat(amount) || 0), 0);
  };

  const handlePayVendorBills = async () => {
    if (payingBills) return; // Prevent double-click
    
    const selectedIds = Object.keys(selectedBills);
    if (selectedIds.length === 0) {
      toast.error('Please select at least one bill');
      return;
    }

    const currentId = closingId || existingClosingId;
    if (!currentId) {
      toast.error('Please save the day closing first');
      return;
    }

    try {
      setPayingBills(true);
      const token = localStorage.getItem('token');
      
      const billIds = selectedIds;
      const amounts = billIds.map(id => parseFloat(billPaymentAmounts[id]) || 0);
      
      // 1. First create deductions for each payment (to get deduction IDs)
      const deductionIds = [];
      for (let i = 0; i < billIds.length; i++) {
        const bill = payableBills.find(b => b.id === billIds[i]);
        const amount = amounts[i];
        if (bill && amount > 0) {
          const response = await axios.post(`${API}/dayclosings/${currentId}/deductions`, {
            category: `VENDOR PAYMENT - ${bill.vendor_name}`,
            amount: amount,
            remarks: `Bill #${bill.bill_number}`
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deductionIds.push(response.data.id);
        } else {
          deductionIds.push(null);
        }
      }

      // 2. Pay the vendor bills with dayclosing_id and deduction_ids
      await axios.post(`${API}/purchase-bills/pay`, {
        bill_ids: billIds,
        amounts: amounts,
        payment_category: 'current_month',
        payment_method: 'cash',
        remarks: `Paid from Day Closing ${closingData.date}`,
        dayclosing_id: currentId,
        deduction_ids: deductionIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Vendor bills paid and deductions added');
      setVendorBillDialog(false);
      setSelectedBills({});
      setBillPaymentAmounts({});
      
      // Refresh data
      const idToUse = closingId || existingClosingId;
      fetchDeductions(idToUse);
      fetchClosing(idToUse);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to pay vendor bills'));
    } finally {
      setPayingBills(false);
    }
  };

  const openDeleteDeductionDialog = (deductionId, categoryName) => {
    setDeductionToDelete({ id: deductionId, category: categoryName });
    setDeleteDeductionDialog(true);
  };

  const handleDeductionDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = deductions.findIndex(d => d.id === active.id);
    const newIndex = deductions.findIndex(d => d.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedDeductions = arrayMove(deductions, oldIndex, newIndex);
      setDeductions(reorderedDeductions);

      // Send reorder to backend
      try {
        const token = localStorage.getItem('token');
        const currentId = closingId || existingClosingId;
        await axios.post(`${API}/dayclosings/${currentId}/deductions/reorder`, 
          reorderedDeductions.map(d => d.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        toast.error('Failed to save order');
        fetchDeductions(closingId || existingClosingId); // Revert on error
      }
    }
  };

  const handleCloseDay = async () => {
    if (closingDay) return; // Prevent double-click
    
    try {
      setClosingDay(true);
      const token = localStorage.getItem('token');
      const currentId = closingId || existingClosingId;
      await axios.post(`${API}/dayclosings/${currentId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Day closing submitted for approval!');
      setCloseDayDialog(false);
      navigate('/');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to close day'));
    } finally {
      setClosingDay(false);
    }
  };

  const isEditable = closingData.status === 'draft' || user.role === 'admin';
  const currentId = closingId || existingClosingId;
  const canAddDeductions = currentId && (closingData.status === 'draft' || user.role === 'admin');

  return (
    <div className="space-y-6" data-testid="day-closing-form">
      {/* Status Banner */}
      {closingData.status !== 'draft' && (
        <Card className={`border-l-4 ${
          closingData.status === 'closed' ? 'border-l-blue-500 bg-blue-50' : 'border-l-green-500 bg-green-50'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center">
              {closingData.status === 'closed' && (
                <>
                  <Lock className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="font-semibold text-blue-900">This day closing is locked and waiting for admin approval</span>
                </>
              )}
              {closingData.status === 'approved' && (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-semibold text-green-900">This day closing has been approved</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sales Information */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Sales Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={closingData.date}
                onChange={(e) => setClosingData({ ...closingData, date: e.target.value })}
                disabled={!!currentId || !isEditable}
                data-testid="date-input"
              />
            </div>
            {closingData.outlet && (
              <div className="space-y-2">
                <Label>Outlet</Label>
                <Input
                  value={closingData.outlet}
                  disabled
                  data-testid="outlet-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Opening Cash (₹)</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  value={closingData.opening_cash}
                  onChange={(e) => setClosingData({ ...closingData, opening_cash: e.target.value })}
                  disabled={user.role === 'cashier' || !isEditable}
                  readOnly={user.role === 'cashier'}
                  className={user.role === 'cashier' ? 'bg-gray-100 cursor-not-allowed' : ''}
                  data-testid="opening-cash-input"
                />
                {user.role === 'cashier' && (
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from previous day&apos;s closing cash</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cash Received from Bank (₹)</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  value={closingData.cash_received || 0}
                  readOnly
                  className="bg-green-50 cursor-not-allowed border-green-200"
                  data-testid="cash-received-input"
                />
                <p className="text-xs text-green-600 mt-1">Auto-filled from bank cash withdrawals for this date</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Gross Sales (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={closingData.gross_sales}
                onChange={(e) => setClosingData({ ...closingData, gross_sales: e.target.value })}
                disabled={!isEditable}
                data-testid="gross-sales-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Extra Liquor Sale (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={closingData.extra_liquor_sale}
                onChange={(e) => setClosingData({ ...closingData, extra_liquor_sale: e.target.value })}
                disabled={!isEditable}
                data-testid="extra-liquor-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Extra Other Sale (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={closingData.extra_other_sale}
                onChange={(e) => setClosingData({ ...closingData, extra_other_sale: e.target.value })}
                disabled={!isEditable}
                data-testid="extra-other-input"
              />
            </div>
            <div className="space-y-2">
              <Label>NC Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={closingData.nc_amount}
                onChange={(e) => setClosingData({ ...closingData, nc_amount: e.target.value })}
                disabled={!isEditable}
                className="border-purple-200 focus:border-purple-400"
                data-testid="nc-amount-input"
              />
              <p className="text-xs text-purple-600">No Charge / Non-Chargeable (Add-on to total sales)</p>
            </div>
            <div className="space-y-2">
              <Label>Discounts Given (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={closingData.discounts_given}
                onChange={(e) => setClosingData({ ...closingData, discounts_given: e.target.value })}
                disabled={!isEditable}
                data-testid="discounts-given-input"
              />
              <p className="text-xs text-gray-500">Add-on to total sales</p>
            </div>
            
            {/* Cash Deposit to Bank 592 */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Cash Deposit to Bank 592 (₹)</span>
                <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Internal Transfer</Badge>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={closingData.cash_deposit_to_bank || 0}
                onChange={(e) => setClosingData({ ...closingData, cash_deposit_to_bank: parseFloat(e.target.value) || 0 })}
                disabled={!isEditable || depositingCash}
                className="border-blue-200 focus:border-blue-400"
                data-testid="cash-deposit-to-bank-input"
              />
              <p className="text-xs text-blue-600">
                Cash deposited to Bank 592 • Reduces closing cash • Not counted as expense
              </p>
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Total Sales</p>
            <p className="text-3xl font-bold text-green-700" style={{ fontFamily: 'Space Grotesk' }}>
              ₹{closingData.total_sales.toFixed(2)}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={closingData.notes}
              onChange={(e) => setClosingData({ ...closingData, notes: e.target.value })}
              disabled={!isEditable}
              placeholder="Any additional notes..."
              data-testid="notes-input"
            />
          </div>

          {isEditable && (
            <Button onClick={() => handleSaveClosing()} disabled={loading} data-testid="save-btn">
              {loading ? 'Saving...' : currentId ? 'Update Day Closing' : 'Create Day Closing'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Deductions */}
      {currentId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Deductions</CardTitle>
              {canAddDeductions && <p className="text-sm text-gray-500 mt-1">Drag to reorder deductions</p>}
            </div>
            {canAddDeductions && (
              <div className="flex gap-2">
                <Dialog open={deductionDialog} onOpenChange={(open) => {
                  setDeductionDialog(open);
                  if (!open) {
                    setDeductionForm({ group: '', category: '', amount: 0, remarks: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-deduction-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Deduction
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Deduction</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddDeduction} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Group</Label>
                      <Select
                        value={deductionForm.group}
                        onValueChange={(value) => setDeductionForm({ ...deductionForm, group: value, category: '' })}
                      >
                        <SelectTrigger data-testid="deduction-group-select">
                          <SelectValue placeholder="Select group first" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...new Set(categories.map(c => c.group))]
                            .filter(group => group.toLowerCase() !== 'vendor payments')
                            .sort()
                            .map((group) => (
                              <SelectItem key={group} value={group}>
                                {group}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={deductionForm.category}
                        onValueChange={(value) => setDeductionForm({ ...deductionForm, category: value })}
                        disabled={!deductionForm.group}
                      >
                        <SelectTrigger data-testid="deduction-category-select">
                          <SelectValue placeholder={deductionForm.group ? "Select category" : "Select group first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories
                            .filter(cat => cat.group === deductionForm.group)
                            .map((cat) => (
                              <SelectItem key={cat.id} value={cat.name}>
                                {cat.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {categories.length === 0 && (
                        <p className="text-sm text-amber-600">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          No categories defined. Admin should add categories in Settings.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Amount (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={deductionForm.amount}
                        onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                        required
                        data-testid="deduction-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Remarks (optional)</Label>
                      <Textarea
                        value={deductionForm.remarks}
                        onChange={(e) => setDeductionForm({ ...deductionForm, remarks: e.target.value })}
                        placeholder="Additional details..."
                        data-testid="deduction-remarks-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-deduction-btn" disabled={!deductionForm.category || addingDeduction}>
                      {addingDeduction ? 'Adding...' : 'Add Deduction'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Single Pay Vendor Bills Button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={openVendorBillDialog} 
                data-testid="pay-vendor-bills-btn"
                className="bg-green-50 hover:bg-green-100 border-green-200"
              >
                <Package className="w-4 h-4 mr-2" />
                Pay Vendor Bills
                {(todayBills.length + thisMonthBills.length + oldMonthBills.length) > 0 && (
                  <Badge className="ml-2 bg-green-600">
                    {todayBills.length + thisMonthBills.length + oldMonthBills.length}
                  </Badge>
                )}
              </Button>

              {/* Vendor Bill Payment Dialog */}
              <Dialog open={vendorBillDialog} onOpenChange={setVendorBillDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="vendor-bill-dialog-description">
                  <DialogHeader>
                    <DialogTitle>Pay Vendor Bills</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p id="vendor-bill-dialog-description" className="text-sm text-gray-500">
                      Select period and bills to pay. Payment will be deducted from closing cash.
                    </p>
                    
                    {/* Tab selector inside dialog */}
                    <div className="flex gap-2 border-b pb-2">
                      <Button 
                        variant={vendorBillType === 'today' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => {
                          setVendorBillType('today');
                          setSelectedVendorFilter('');
                        }}
                        className={vendorBillType === 'today' ? 'bg-green-600 hover:bg-green-700' : ''}
                      >
                        Today ({todayBills.length})
                      </Button>
                      <Button 
                        variant={vendorBillType === 'this_month' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => {
                          setVendorBillType('this_month');
                          setSelectedVendorFilter('');
                        }}
                        className={vendorBillType === 'this_month' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      >
                        This Month ({thisMonthBills.length})
                      </Button>
                      <Button 
                        variant={vendorBillType === 'old_month' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => {
                          setVendorBillType('old_month');
                          setSelectedVendorFilter('');
                        }}
                        className={vendorBillType === 'old_month' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                      >
                        Old Months ({oldMonthBills.length})
                      </Button>
                    </div>
                    
                    {/* Vendor Filter for This Month and Old Month */}
                    {(vendorBillType === 'this_month' || vendorBillType === 'old_month') && (
                      <div className="bg-gray-50 p-3 rounded-lg border">
                        <Label className="text-sm font-medium mb-2 block">Select Vendor to View Bills</Label>
                        <Select value={selectedVendorFilter} onValueChange={setSelectedVendorFilter}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Choose a vendor..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableVendors
                              .filter(v => {
                                // Only show vendors that have bills in the selected period
                                const bills = vendorBillType === 'this_month' ? thisMonthBills : oldMonthBills;
                                return bills.some(b => b.vendor_id === v.id);
                              })
                              .map(vendor => (
                                <SelectItem key={vendor.id} value={vendor.id}>
                                  {vendor.name} ({vendorBillType === 'this_month' 
                                    ? thisMonthBills.filter(b => b.vendor_id === vendor.id).length 
                                    : oldMonthBills.filter(b => b.vendor_id === vendor.id).length} bills)
                                </SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                        {!selectedVendorFilter && (
                          <p className="text-xs text-amber-600 mt-2">
                            Please select a vendor to view and pay their bills
                          </p>
                        )}
                      </div>
                    )}
                    
                    {loadingBills ? (
                      <div className="text-center py-4">Loading bills...</div>
                    ) : vendorBillType === 'today' || selectedVendorFilter ? (
                      payableBills.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          No {vendorBillType === 'today' ? "today's" : vendorBillType === 'this_month' ? "this month's" : "old month"} bills to pay
                          {selectedVendorFilter && ' for selected vendor'}
                        </div>
                      ) : (
                        <div className="max-h-64 overflow-y-auto border rounded">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Vendor</TableHead>
                                <TableHead>Bill No.</TableHead>
                                <TableHead>Bill Date</TableHead>
                                <TableHead className="text-right">Balance</TableHead>
                                <TableHead className="text-right">Pay Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {payableBills.map((bill) => (
                                <TableRow key={bill.id} className={selectedBills[bill.id] ? 'bg-blue-50' : ''}>
                                  <TableCell>
                                    <Checkbox 
                                      checked={!!selectedBills[bill.id]}
                                      onCheckedChange={() => toggleBillSelection(bill.id, bill.balance)}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">{bill.vendor_name}</TableCell>
                                  <TableCell>{bill.bill_number}</TableCell>
                                  <TableCell className="whitespace-nowrap text-sm text-gray-600" data-testid={`bill-date-${bill.id}`}>{formatBillDate(bill.bill_date)}</TableCell>
                                  <TableCell className="text-right text-red-600">
                                    ₹{bill.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {selectedBills[bill.id] ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        className="w-24 text-right"
                                        value={billPaymentAmounts[bill.id] || ''}
                                        onChange={(e) => updateBillPaymentAmount(bill.id, e.target.value, bill.balance)}
                                      />
                                    ) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-8 text-gray-400 border rounded bg-gray-50">
                        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Select a vendor above to view bills</p>
                      </div>
                    )}

                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span className="font-medium">Total Payment:</span>
                      <span className="text-xl font-bold text-green-600">
                        ₹{getTotalBillPayment().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setVendorBillDialog(false)}>Cancel</Button>
                      <Button 
                        onClick={handlePayVendorBills} 
                        disabled={Object.keys(selectedBills).length === 0 || payingBills}
                      >
                        {payingBills ? 'Processing...' : 'Pay Selected Bills'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Pay Opening Liabilities Button */}
              <Button variant="outline" onClick={openLiabilityDialog} className="bg-purple-50" data-testid="pay-opening-liabilities-btn">
                <Wallet className="w-4 h-4 mr-2" />
                Pay Opening Liabilities
              </Button>

              {/* Opening Liabilities Payment Dialog */}
              <Dialog open={liabilityDialog} onOpenChange={(open) => !payingLiabilities && setLiabilityDialog(open)}>
                <DialogContent className="max-w-2xl" aria-describedby="liability-dialog-description">
                  <DialogHeader>
                    <DialogTitle>Pay Opening Year Liabilities</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p id="liability-dialog-description" className="text-sm text-gray-500">
                      Select opening liabilities to pay with cash. Payment will be deducted from closing cash.
                    </p>
                    
                    {openingLiabilities.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">No opening liabilities found</div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto border rounded">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10"></TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Remarks</TableHead>
                              <TableHead className="text-right">Balance</TableHead>
                              <TableHead className="text-right">Pay Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {openingLiabilities.map((liability) => (
                              <TableRow key={liability.id} className={selectedLiabilities[liability.id] ? 'bg-purple-50' : ''}>
                                <TableCell>
                                  <Checkbox 
                                    checked={!!selectedLiabilities[liability.id]}
                                    onCheckedChange={() => toggleLiabilitySelection(liability.id, liability.balance)}
                                    disabled={payingLiabilities}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{liability.entity_name || liability.vendor_name || 'Unknown'}</TableCell>
                                <TableCell className="text-sm text-gray-500">{liability.remarks || '-'}</TableCell>
                                <TableCell className="text-right text-red-600">
                                  ₹{liability.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right">
                                  {selectedLiabilities[liability.id] ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      className="w-24 text-right"
                                      value={liabilityPaymentAmounts[liability.id] || ''}
                                      onChange={(e) => updateLiabilityPaymentAmount(liability.id, e.target.value, liability.balance)}
                                    />
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setLiabilityDialog(false)} disabled={payingLiabilities}>Cancel</Button>
                      <Button 
                        onClick={paySelectedLiabilities} 
                        disabled={Object.keys(selectedLiabilities).length === 0 || payingLiabilities}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {payingLiabilities ? 'Processing...' : 'Pay Selected Liabilities'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Pay Salaries Button */}
              <Button 
                variant="outline"
                onClick={openSalaryDialog} 
                className="bg-yellow-50 hover:bg-yellow-100 border-yellow-200" 
                data-testid="pay-salaries-btn"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Pay Salaries
                {(thisMonthSalaries.length + oldMonthSalaries.length) > 0 && (
                  <Badge className="ml-2 bg-yellow-600">
                    {thisMonthSalaries.length + oldMonthSalaries.length}
                  </Badge>
                )}
              </Button>

              {/* Salary Payment Dialog */}
              <Dialog open={salaryDialog} onOpenChange={setSalaryDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="salary-dialog-description">
                  <DialogHeader>
                    <DialogTitle>Pay Salaries</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p id="salary-dialog-description" className="text-sm text-gray-500">
                      Select salaries to pay or give advance. Payment will be deducted from closing cash.
                    </p>
                    
                    {/* Tab selector inside dialog */}
                    <div className="flex gap-2 border-b pb-2">
                      <Button 
                        variant={salaryPaymentType === 'this_month' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => setSalaryPaymentType('this_month')}
                        className={salaryPaymentType === 'this_month' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      >
                        This Month ({thisMonthSalaries.length})
                      </Button>
                      <Button 
                        variant={salaryPaymentType === 'old_months' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => setSalaryPaymentType('old_months')}
                        className={salaryPaymentType === 'old_months' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                      >
                        Old Months ({oldMonthSalaries.length})
                      </Button>
                      <Button 
                        variant={salaryPaymentType === 'advance' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => setSalaryPaymentType('advance')}
                        className={salaryPaymentType === 'advance' ? 'bg-green-600 hover:bg-green-700' : ''}
                      >
                        Advance Salary
                      </Button>
                    </div>
                    
                    {loadingSalaries ? (
                      <div className="text-center py-4">Loading salary data...</div>
                    ) : salaryPaymentType === 'advance' ? (
                      /* Advance Salary Form */
                      <div className="space-y-4">
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700 mb-4">
                            Advance salary will be added to employee's advance balance and will be auto-deducted when generating next month's payroll.
                          </p>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-sm font-medium mb-1 block">Select Employee</Label>
                              <Select value={advanceEmployee} onValueChange={setAdvanceEmployee}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose employee..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {employeesForAdvance.filter(emp => emp.status === 'active').map(emp => (
                                    <SelectItem key={emp.id} value={emp.id}>
                                      {emp.name} - {emp.designation} 
                                      {emp.advance_balance > 0 && ` (Existing: ₹${emp.advance_balance.toLocaleString()})`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-medium mb-1 block">Advance Amount</Label>
                              <Input
                                type="number"
                                step="100"
                                placeholder="Enter amount..."
                                value={advanceAmount}
                                onChange={(e) => setAdvanceAmount(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => setSalaryDialog(false)}>Cancel</Button>
                          <Button 
                            onClick={handlePayAdvance} 
                            disabled={!advanceEmployee || !advanceAmount || parseFloat(advanceAmount) <= 0 || payingSalaries}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {payingSalaries ? 'Processing...' : 'Pay Advance'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Salary Payment List */
                      <>
                        {(salaryPaymentType === 'this_month' ? thisMonthSalaries : oldMonthSalaries).length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            No {salaryPaymentType === 'this_month' ? "this month" : "old months"} salaries to pay
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto border rounded">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-10"></TableHead>
                                  <TableHead>Employee</TableHead>
                                  <TableHead>Month</TableHead>
                                  <TableHead className="text-right">Net Salary</TableHead>
                                  <TableHead className="text-right">Balance</TableHead>
                                  <TableHead className="text-right">Pay Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(salaryPaymentType === 'this_month' ? thisMonthSalaries : oldMonthSalaries).map((salary) => (
                                  <TableRow key={salary.id} className={selectedSalaries[salary.id] ? 'bg-yellow-50' : ''}>
                                    <TableCell>
                                      <Checkbox 
                                        checked={!!selectedSalaries[salary.id]}
                                        onCheckedChange={() => toggleSalarySelection(salary.id, salary.balance)}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium">{salary.employee_name}</TableCell>
                                    <TableCell className="text-sm">{salary.salary_month}</TableCell>
                                    <TableCell className="text-right">
                                      ₹{salary.net_salary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell className="text-right text-red-600">
                                      ₹{salary.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {selectedSalaries[salary.id] ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          className="w-24 text-right"
                                          value={salaryPaymentAmounts[salary.id] || ''}
                                          onChange={(e) => updateSalaryPaymentAmount(salary.id, e.target.value, salary.balance)}
                                        />
                                      ) : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}

                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                          <span className="font-medium">Total Payment:</span>
                          <span className="text-xl font-bold text-yellow-600">
                            ₹{getTotalSalaryPayment().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        </div>

                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => setSalaryDialog(false)}>Cancel</Button>
                          <Button 
                            onClick={handlePaySalaries} 
                            disabled={Object.keys(selectedSalaries).length === 0 || payingSalaries}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            {payingSalaries ? 'Processing...' : 'Pay Selected Salaries'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              {/* Pay Utility Bills Button */}
              <Button 
                variant="outline" 
                onClick={openUtilityBillDialog} 
                className="bg-cyan-50 hover:bg-cyan-100 border-cyan-200" 
                data-testid="pay-utility-bills-btn"
              >
                <Package className="w-4 h-4 mr-2" />
                Pay Utility Bills
              </Button>

              {/* Utility Bill Payment Dialog */}
              <Dialog open={utilityBillDialog} onOpenChange={setUtilityBillDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="utility-dialog-description">
                  <DialogHeader>
                    <DialogTitle>Pay Utility Bills</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p id="utility-dialog-description" className="text-sm text-gray-500">
                      Select utility bills to pay. Payment will be deducted from closing cash.
                    </p>
                    
                    {loadingUtilityBills ? (
                      <div className="text-center py-4">Loading utility bills...</div>
                    ) : pendingUtilityBills.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        No pending utility bills found
                      </div>
                    ) : (
                      <>
                        <div className="max-h-64 overflow-y-auto border rounded">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead>Utility Type</TableHead>
                                <TableHead>Month</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="text-right">Balance</TableHead>
                                <TableHead className="text-right">Pay Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pendingUtilityBills.map((bill) => (
                                <TableRow key={bill.id} className={selectedUtilityBills[bill.id] ? 'bg-cyan-50' : ''}>
                                  <TableCell>
                                    <Checkbox 
                                      checked={!!selectedUtilityBills[bill.id]}
                                      onCheckedChange={() => toggleUtilityBillSelection(bill.id, bill.balance)}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">{bill.bill_type_label || bill.bill_type}</TableCell>
                                  <TableCell>{bill.bill_month}</TableCell>
                                  <TableCell className="text-right">₹{bill.amount?.toLocaleString()}</TableCell>
                                  <TableCell className="text-right font-medium text-red-600">₹{bill.balance?.toLocaleString()}</TableCell>
                                  <TableCell className="text-right">
                                    {selectedUtilityBills[bill.id] ? (
                                      <Input
                                        type="number"
                                        step="100"
                                        className="w-24 text-right"
                                        value={utilityPaymentAmounts[bill.id] || ''}
                                        onChange={(e) => updateUtilityPaymentAmount(bill.id, e.target.value, bill.balance)}
                                      />
                                    ) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="font-semibold">Total Payment:</span>
                          <span className="text-xl font-bold text-cyan-600">₹{getTotalUtilityPayment().toLocaleString()}</span>
                        </div>
                        
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => setUtilityBillDialog(false)}>Cancel</Button>
                          <Button 
                            onClick={handlePayUtilityBills} 
                            disabled={Object.keys(selectedUtilityBills).length === 0 || payingUtilityBills}
                            className="bg-cyan-600 hover:bg-cyan-700"
                          >
                            {payingUtilityBills ? 'Processing...' : 'Pay Selected Utility Bills'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {deductions.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No deductions added yet</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDeductionDragEnd}>
                <SortableContext items={deductions.map(d => d.id)} strategy={verticalListSortingStrategy}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {canAddDeductions && <TableHead className="w-10"></TableHead>}
                        <TableHead>Category</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Remarks</TableHead>
                        {canAddDeductions && <TableHead>Action</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deductions.map((deduction) => (
                        <SortableDeductionRow
                          key={deduction.id}
                          deduction={deduction}
                          canEdit={canAddDeductions}
                          onDelete={openDeleteDeductionDialog}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {currentId && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Day Closing Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">Opening Cash</span>
                <span className="font-bold">₹{parseFloat(closingData.opening_cash || 0).toFixed(2)}</span>
              </div>
              {parseFloat(closingData.cash_received || 0) > 0 && (
                <div className="flex justify-between items-center pb-2 border-b bg-green-50 -mx-6 px-6 py-1">
                  <span className="text-green-700">Cash Received (Bank Withdrawal)</span>
                  <span className="font-bold text-green-600">+ ₹{parseFloat(closingData.cash_received || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">Total Sales</span>
                <span className="font-bold text-green-600">+ ₹{closingData.total_sales.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">Total Deductions</span>
                <span className="font-bold text-red-600">- ₹{closingData.total_deductions.toFixed(2)}</span>
              </div>
              {parseFloat(closingData.cash_deposit_to_bank || 0) > 0 && (
                <div className="flex justify-between items-center pb-2 border-b bg-blue-50 -mx-6 px-6 py-1">
                  <span className="text-blue-700">Cash Deposit to Bank 592</span>
                  <span className="font-bold text-blue-600">- ₹{parseFloat(closingData.cash_deposit_to_bank || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2">
                <span className="text-lg font-semibold">Closing Cash</span>
                <span className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>
                  ₹{closingData.closing_cash.toFixed(2)}
                </span>
              </div>
            </div>

            {closingData.status === 'draft' && user.role === 'cashier' && (
              <div className="mt-6">
                <Button 
                  onClick={() => setCloseDayDialog(true)} 
                  className="w-full" 
                  size="lg"
                  data-testid="close-day-btn"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Close Day & Submit for Approval
                </Button>
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Once closed, you will not be able to edit this entry
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Close Day Confirmation Dialog */}
      <AlertDialog open={closeDayDialog} onOpenChange={setCloseDayDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Day & Submit for Approval?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to close this day? Once submitted, you will not be able to edit this entry.
              An admin will review and approve your submission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closingDay}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCloseDay}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={closingDay}
            >
              {closingDay ? 'Submitting...' : 'Submit for Approval'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Deduction Confirmation Dialog */}
      <AlertDialog open={deleteDeductionDialog} onOpenChange={setDeleteDeductionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deduction?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the deduction for &quot;<strong>{deductionToDelete?.category}</strong>&quot;? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDeduction}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteDeduction(deductionToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingDeduction}
            >
              {deletingDeduction ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Remarks Dialog */}
      <AlertDialog open={editRemarksDialog} onOpenChange={setEditRemarksDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Remarks Required</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Please provide a reason for modifying this day closing:</p>
                <Textarea
                  value={editRemarks}
                  onChange={(e) => setEditRemarks(e.target.value)}
                  placeholder="e.g., Corrected sales figures, Added missing deduction, etc."
                  rows={3}
                  data-testid="edit-remarks-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setEditRemarks('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSaveWithRemarks}
              disabled={!editRemarks.trim()}
              data-testid="save-remarks-btn"
            >
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DayClosingForm;